import { combineSlices, createSelector } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { emailUserSlice } from './emailUsers'
import { emailFilterSlice } from './emailFilters'
import { emailMetadataSlice, selectEmailMetadata, selectEmailMetadataLoading } from './emailMetadata'
import { emailTypeSlice } from './emailTypes'
import { emailThreadSlice, selectEmailThreads, selectEmailThreadsLoading } from './emailThreads'
import { emailResponseSlice } from './emailResponse'
import { emailResponseClientProfileSlice } from './emailResponseClientProfile'
import { newEmailSlice } from './newEmail'
import { emailAttachmentsSlice } from './emailAttachments'
import { emailSidebarSlice } from './emailSidebar'
import { selectTasksLoading } from '../task/task'
import { hasTenMinutesPassed } from '../utils'
import { selectBusinessEventsLoading } from '../businessEvent/businessEvent'

export default combineSlices(
  emailUserSlice,
  emailFilterSlice,
  emailMetadataSlice,
  emailTypeSlice,
  emailThreadSlice,
  emailResponseSlice,
  emailResponseClientProfileSlice,
  newEmailSlice,
  emailAttachmentsSlice,
  emailSidebarSlice,
)

export const selectEmailHubLoading = (state: RootState) => {
  const {
    emailHub: { emailUsers, emailFilters, emailMetadata, emailTypes, emailThreads },
  } = state

  return (
    emailUsers.isCurrentUserLoading ||
    emailUsers.contactsLoading ||
    emailFilters.isLoading ||
    emailMetadata.isLoading ||
    emailTypes.isLoading ||
    emailThreads.isLoading
  )
}

export const selectShouldPollEmailThreadTasks = createSelector(
  [selectTasksLoading, selectEmailMetadataLoading, selectEmailMetadata, selectEmailThreadsLoading, selectEmailThreads, (_, threadId) => threadId],
  (tasksLoading, emailMetadataLoading, emailMetadata, emailThreadsLoading, emailThreads, threadId) => {
    if (tasksLoading || emailMetadataLoading || emailThreadsLoading) {
      return false
    }

    const emailThread = emailThreads.find((emailThread) => emailThread.externalId === threadId)
    if (!emailThread) {
      return false
    }

    const emailIds = emailThread.emails.map((email) => email.externalId)
    const emailMetadataForThread = emailMetadata.filter((metadata) => emailIds.includes(metadata.externalId))

    const processingFinishedForAllEmailsInThread = emailMetadataForThread.every((metadata) => metadata.taskProcessing.finishedAt)
    if (processingFinishedForAllEmailsInThread) {
      return false
    }

    const processingTimeoutExpiredForAllEmailsInThread = emailMetadataForThread.every(
      (metadata) => metadata.taskProcessing.startedAt && hasTenMinutesPassed(metadata.taskProcessing.startedAt),
    )
    return !processingTimeoutExpiredForAllEmailsInThread
  },
)

export const selectShouldPollEmailThreadBusinessEvents = createSelector(
  [
    selectBusinessEventsLoading,
    selectEmailMetadataLoading,
    selectEmailMetadata,
    selectEmailThreadsLoading,
    selectEmailThreads,
    (_, threadId: string) => threadId,
  ],
  (businessEventsLoading, emailMetadataLoading, emailMetadata, emailThreadsLoading, emailThreads, threadId) => {
    if (businessEventsLoading || emailMetadataLoading || emailThreadsLoading) {
      return false
    }

    const emailThread = emailThreads.find((emailThread) => emailThread.externalId === threadId)
    if (!emailThread) {
      return false
    }

    const emailIds = emailThread.emails.map((email) => email.externalId)

    const emailMetadataForThread = emailMetadata.filter((metadata) => emailIds.includes(metadata.externalId))

    const processingFinishedForAllEmailsInThread = emailMetadataForThread.every((metadata) => metadata.businessEventProcessing.finishedAt)
    if (processingFinishedForAllEmailsInThread) {
      return false
    }

    const processingTimeoutExpiredForAllEmailsInThread = emailMetadataForThread.every(
      (metadata) => metadata.businessEventProcessing.startedAt && hasTenMinutesPassed(metadata.businessEventProcessing.startedAt),
    )
    return !processingTimeoutExpiredForAllEmailsInThread
  },
)
